/**
 * Lo que mide el gestor de red EN EL TELÉFONO · punto 10, revisión independiente 🟠1 (4-nov-2026).
 *
 * El arreglo de `gestorDeRed.js` parte de una causa leída en el código, no medida en un iPhone; y es la segunda vez
 * que se arregla «hay que salir y entrar» (la primera, 24-sep: la bandeja no tenía oyente). La regla del repo: al
 * segundo arreglo se instrumenta, y se mide el RESULTADO, no la rama tomada. El resultado aquí es si, tras cada
 * reconexión, la bandeja volvió a oír al servidor (un snapshot con `fromCache === false`) y cuánto tardó.
 *
 * Se guarda en el propio teléfono (`localStorage`, las últimas 50) y, con la bandeja abierta en una organización,
 * también en `organizations/{orgId}/diagnosticoRed/{uid}`: una app instalada en iPhone no tiene barra de direcciones
 * ni consola, y su almacenamiento no es el de Safari, así que lo local sólo se ve en escritorio (`?diagnosticoRed=1`);
 * lo del servidor se lee con la credencial de solo lectura (segunda revisión independiente, 🟠2). Funciones PURAS, en
 * CommonJS, probadas desde `functions/__tests__`.
 */
const MAX_ENTRADAS = 50;
/** Lo que se espera al servidor tras reconectar antes de anotar que no llegó. */
const ESPERA_SERVIDOR_MS = 15000;

/** La lista con la entrada al final, recortada a las últimas `max`. Tolera una lista ilegible (la descarta). */
function anotar(lista, entrada, max = MAX_ENTRADAS) {
  return [...(Array.isArray(lista) ? lista : []), entrada].slice(-max);
}

/**
 * Como `anotar`, pero un error idéntico al último (mismo evento, paso y texto) suma `veces` en vez de ocupar otra
 * fila: si la cola de Firestore falla, cada operación siguiente rechaza igual, y 50 copias expulsarían del buffer las
 * reconexiones, que son lo que se quiere leer (segunda revisión, 🟡4).
 */
function anotarAgrupando(lista, entrada, max = MAX_ENTRADAS) {
  const previa = Array.isArray(lista) ? lista : [];
  const ultima = previa[previa.length - 1];
  if (ultima && entrada.error && ultima.evento === entrada.evento && ultima.paso === entrada.paso && ultima.error === entrada.error) {
    return [...previa.slice(0, -1), { ...ultima, veces: (ultima.veces || 1) + 1, ultimaVez: entrada.en }];
  }
  return anotar(previa, entrada, max);
}

/** La lista con los campos añadidos a la entrada `id` (si ya salió del buffer, sin cambios). */
function completar(lista, id, campos) {
  return (Array.isArray(lista) ? lista : []).map((e) => (e && e.id === id ? { ...e, ...campos } : e));
}

/**
 * El resultado de una reconexión.
 * @param {{ desdeMs: number, servidorEnMs: number|null, conOyente: boolean, interrumpida?: boolean }} e
 *   `desdeMs`: cuando la reconexión TERMINÓ (un snapshot que el canal viejo dejó en vuelo no cuenta, 🟡2);
 *   `servidorEnMs`: cuándo llegó el primer snapshot del servidor desde entonces (null: no llegó en la espera);
 *   `conOyente`: si la bandeja estaba montada (fuera de ella no hay con qué medir, y eso no es un fallo);
 *   `interrumpida`: la app se ocultó o salió de la bandeja antes de saberlo (congelada no es «sin servidor», 🟡1).
 */
function resultadoDeReconexion({ desdeMs, servidorEnMs, conOyente, interrumpida = false }) {
  if (!conOyente) return { resultado: 'sin-oyente' };
  if (interrumpida) return { resultado: 'interrumpida' };
  if (servidorEnMs == null || servidorEnMs < desdeMs) return { resultado: 'sin-servidor' };
  return { resultado: 'ok', servidorTrasMs: servidorEnMs - desdeMs };
}

/** ¿Un error suelto de la página es de Firestore o de su caché? (iOS puede perder IndexedDB tras el segundo plano.) */
function esErrorDeRed(mensaje) {
  return /firestore|indexeddb|indexed database|asyncqueue/i.test(String(mensaje || ''));
}

module.exports = { anotar, anotarAgrupando, completar, resultadoDeReconexion, esErrorDeRed, MAX_ENTRADAS, ESPERA_SERVIDOR_MS };
